import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { BaseButtonProps } from "shared/atoms/BaseButton/BaseButton";
import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";
import { Notification } from "shared/atoms/Notification";

import { BaseButton } from "../BaseButton";

interface Props {
  body?: string;
  primaryButtonProps?: BaseButtonProps;
  content?: React.ReactNode;
  dataTestID?: string;
  error?: string;
  isLoading?: boolean;
  isOpen: boolean;
  title: string;
  showCloseButton?: boolean;
  onCancel?: () => void;
  onClickOutside?: () => void;
  onConfirm?: () => void;
}

export const ConfirmationPopup: React.FC<Props> = ({
  body,
  primaryButtonProps,
  content,
  dataTestID,
  error,
  isLoading,
  isOpen,
  title,
  showCloseButton,
  onCancel,
  onClickOutside,
  onConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <MUIUnifiedModal open={isOpen} onClose={() => onClickOutside?.()} width="500px" showCloseButton={showCloseButton}>
      <PopupContent data-testid={dataTestID}>
        <Header>{title}</Header>
        {body && <div>{body}</div>}
        {content || (
          <Buttons>
            <BaseButton
              dataTestID={`${dataTestID}-button-confirm`}
              text={onCancel ? t("buttons.yes") : t("buttons.ok")}
              onClick={onConfirm}
              disabled={isLoading}
              loading={isLoading}
              {...primaryButtonProps}
            />
            {onCancel && (
              <BaseButton
                dataTestID={`${dataTestID}-button-cancel`}
                text={t("buttons.cancel")}
                onClick={onCancel}
                disabled={isLoading}
                variant="tertiaryBlue"
                uppercase
              />
            )}
          </Buttons>
        )}
        {error && (
          <Notification variant="danger" style={{ marginTop: "16px" }}>
            {error}
          </Notification>
        )}
      </PopupContent>
    </MUIUnifiedModal>
  );
};

const PopupContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  min-width: 430px;
  padding: 16px;
  text-align: center;
  white-space: pre-wrap;

  ${props => props.theme.belowBreakpoint} {
    min-width: 0;
  }
`;

const Header = styled.header`
  color: ${({ theme }) => theme.colors.redesign.db100};
  font-size: 18px;
  font-weight: 700;
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;

  & > button {
    flex: 0;
  }
`;
